import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusPill } from "@/components/StatusBanner";
import { fmtDate, fmtMoney, randToCents } from "@/lib/format";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
export default function TenantDetail() {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [efAmountR, setEfAmountR] = useState("");
  const [efRef, setEfRef] = useState("");
  const [efNote, setEfNote] = useState("");
  const [graceReason, setGraceReason] = useState("");
  const [graceUntil, setGraceUntil] = useState("");
  const [suspendReason, setSuspendReason] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, ev] = await Promise.all([
        api.get(`/platform/tenants/${id}`),
        api.get(`/platform/tenants/${id}/billing/events`, { params: { limit: 20 } }),
      ]);
      setTenant(t.data); setEvents(ev.data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [id]);

  if (loading || !tenant) return <div className="label-overline text-slate-500">Loading…</div>;

  const logEft = async () => {
    try {
      const cents = randToCents(efAmountR);
      if (cents <= 0) return toast.error("Enter an amount in Rand");
      await api.post(`/platform/tenants/${id}/billing/eft`, {
        type: "eft_received", amount_cents: cents,
        reference: efRef, note: efNote,
      });
      toast.success("EFT logged");
      setEfAmountR(""); setEfRef(""); setEfNote("");
      fetchAll();
    } catch (e) { toast.error(formatError(e, "Failed to log EFT")); }
  };

  const setGrace = async () => {
    try {
      await api.post(`/platform/tenants/${id}/grace`, {
        reason: graceReason, grace_until: graceUntil || null
      });
      toast.success("Tenant set to grace");
      fetchAll();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };
  const doSuspend = async () => {
    try {
      await api.post(`/platform/tenants/${id}/suspend`, { reason: suspendReason });
      toast.success("Tenant suspended");
      fetchAll();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };
  const doUnsuspend = async () => {
    try {
      await api.post(`/platform/tenants/${id}/unsuspend`);
      toast.success("Tenant reactivated");
      fetchAll();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };
  const doUpgrade = async () => {
    if (!window.confirm("Upgrade this tenant to the Financial Management plan? This cannot be undone here.")) return;
    try {
      await api.post(`/platform/tenants/${id}/upgrade-financial`);
      toast.success("Upgraded to Financial plan");
      fetchAll();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };
  const doCancelAutopay = async () => {
    const reason = window.prompt(
      "Override — cancel this tenant's PayFast autopay?\n\nThis stops future automatic charges. Local billing state will be set to cancelled and audited.\n\nReason (optional):"
    );
    if (reason === null) return;
    try {
      await api.post(`/platform/tenants/${id}/autopay/cancel`, { reason: reason || null });
      toast.success("Autopay cancelled");
      fetchAll();
    } catch (e) { toast.error(formatError(e, "Cancel failed")); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 label-overline text-slate-500">
        <Link to="/platform/tenants">Tenants</Link>
        <span>/</span>
        <span>{tenant.slug}</span>
      </div>
      <div className="flex items-center gap-4 mt-2 mb-6">
        <h1 className="text-3xl font-black tracking-tight">{tenant.business_name}</h1>
        <StatusPill status={tenant.status} testId="tenant-status" />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="rounded">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-status">Status</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 p-5">
              <div className="label-overline mb-2">Details</div>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div className="text-slate-500">Slug</div><div className="font-mono">{tenant.slug}</div>
                <div className="text-slate-500">Plan</div><div className="font-mono">{tenant.plan}</div>
                <div className="text-slate-500">Cycle</div><div className="font-mono">{tenant.billing_cycle}</div>
                <div className="text-slate-500">Price</div><div className="font-mono">{fmtMoney(tenant.price_cents)}</div>
                <div className="text-slate-500">Discount</div><div className="font-mono">{fmtMoney(tenant.discount_cents)}</div>
                <div className="text-slate-500">Created</div><div className="font-mono">{fmtDate(tenant.created_at)}</div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-5">
              <div className="label-overline mb-2">Feature flags</div>
              <ul className="text-sm font-mono space-y-1">
                {Object.entries(tenant.feature_flags || {}).map(([k, v]) => (
                  <li key={k}><span className="text-slate-500">{k}</span>: <b>{String(v)}</b></li>
                ))}
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 p-5 lg:col-span-1">
              <div className="label-overline mb-3">Log EFT / mark paid</div>
              <div className="space-y-3">
                <div>
                  <Label className="label-overline">Amount (R)</Label>
                  <Input data-testid="eft-amount" className="rounded mt-1 h-10 font-mono"
                         type="text" inputMode="decimal" placeholder="e.g. 499.00"
                         value={efAmountR} onChange={(e) => setEfAmountR(e.target.value)} />
                </div>
                <div>
                  <Label className="label-overline">Reference</Label>
                  <Input data-testid="eft-reference" className="rounded mt-1 h-10 font-mono"
                         value={efRef} onChange={(e) => setEfRef(e.target.value)} />
                </div>
                <div>
                  <Label className="label-overline">Note</Label>
                  <Input data-testid="eft-note" className="rounded mt-1 h-10"
                         value={efNote} onChange={(e) => setEfNote(e.target.value)} />
                </div>
                <Button data-testid="eft-submit" onClick={logEft}
                        className="w-full rounded bg-blue-700 hover:bg-blue-700">Log EFT</Button>
              </div>
            </div>
            <div className="bg-white border border-slate-200 lg:col-span-2">
              <div className="px-4 py-3 border-b border-slate-200 label-overline">Billing events</div>
              <table className="w-full text-sm tbl">
                <thead className="bg-slate-50">
                  <tr className="label-overline text-slate-600">
                    <th className="text-left px-4 py-2">Type</th>
                    <th className="text-right px-4 py-2">Amount</th>
                    <th className="text-left px-4 py-2">Reference</th>
                    <th className="text-left px-4 py-2">Note</th>
                    <th className="text-left px-4 py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No events</td></tr>
                  )}
                  {events.map((e) => (
                    <tr key={e.id} className="border-b border-slate-100">
                      <td className="px-4 py-2 font-mono">{e.type}</td>
                      <td className="px-4 py-2 text-right font-mono">{fmtMoney(e.amount_cents)}</td>
                      <td className="px-4 py-2 font-mono">{e.reference || "—"}</td>
                      <td className="px-4 py-2">{e.note || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs">{fmtDate(e.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <TenantUsers tenantId={id} />
        </TabsContent>

        <TabsContent value="status" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-yellow-300 p-5">
              <div className="label-overline text-yellow-700">Set grace</div>
              <div className="mt-3 space-y-3">
                <Input placeholder="Reason (e.g. Invoice overdue)"
                       data-testid="grace-reason" className="rounded h-10"
                       value={graceReason} onChange={(e) => setGraceReason(e.target.value)} />
                <Input type="date" data-testid="grace-until" className="rounded h-10 font-mono"
                       value={graceUntil} onChange={(e) => setGraceUntil(e.target.value)} />
                <Button data-testid="grace-submit" onClick={setGrace}
                        className="w-full rounded bg-yellow-500 hover:bg-yellow-600 text-black">Set grace</Button>
              </div>
            </div>
            <div className="bg-white border border-red-300 p-5">
              <div className="label-overline text-red-700">Suspend</div>
              <div className="mt-3 space-y-3">
                <Input placeholder="Reason"
                       data-testid="suspend-reason" className="rounded h-10"
                       value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} />
                <Button data-testid="suspend-submit" onClick={doSuspend}
                        className="w-full rounded bg-red-600 hover:bg-red-700">Suspend</Button>
              </div>
            </div>
            <div className="bg-white border border-green-300 p-5">
              <div className="label-overline text-green-700">Reactivate</div>
              <p className="text-xs text-slate-600 mt-2">Clears suspension and grace.</p>
              <Button data-testid="unsuspend-submit" onClick={doUnsuspend}
                      className="w-full mt-3 rounded bg-green-600 hover:bg-green-700">Reactivate</Button>
            </div>
            <div className="bg-white border border-blue-300 p-5 md:col-span-3">
              <div className="label-overline text-blue-700">Upgrade to Financial Plan</div>
              <p className="text-xs text-slate-600 mt-2">Enables ledger, invoices, credit notes, statements, payments received, and customer financial portal.</p>
              <Button data-testid="upgrade-financial" onClick={doUpgrade}
                      className="w-full mt-3 rounded bg-blue-700 hover:bg-blue-800"
                      disabled={tenant.plan === "financial"}>
                {tenant.plan === "financial" ? "Already on Financial plan" : "Upgrade to Financial"}
              </Button>
            </div>
            <div className="bg-white border border-orange-300 p-5 md:col-span-3">
              <div className="label-overline text-orange-700">Cancel PayFast autopay (override)</div>
              <p className="text-xs text-slate-600 mt-2">
                Force-cancel this tenant's recurring PayFast subscription. Local
                billing state moves to <b>cancelled</b>. Data retention rules
                still apply per Cancellation Policy.
              </p>
              <Button data-testid="autopay-cancel-override" onClick={doCancelAutopay}
                      className="w-full mt-3 rounded bg-orange-600 hover:bg-orange-700">
                Cancel autopay for this tenant
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------------
// Stage 3A polish — Platform-side add-user under this tenant.
// Uses existing POST /users which accepts explicit tenant_id from platform.
// -----------------------------------------------------------------------
function TenantUsers({ tenantId }) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [form, setForm] = useState({
    email: "", password: "", role: "truckstop_owner",
  });
  const [submitting, setSubmitting] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  useEffect(() => {
    let c = false;
    setError(null);
    api.get("/users", { params: { tenant_id: tenantId, limit: 100 } })
       .then(({ data }) => { if (!c) setItems(data.items || []); })
       .catch((e) => { if (!c) setError(e); });
    return () => { c = true; };
  }, [tenantId, reloadKey]);

  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let out = "";
    for (let i = 0; i < 12; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
    return out + "!2026";
  };

  const submit = async (e) => {
    e.preventDefault();
    const password = form.password || genPassword();
    setSubmitting(true);
    try {
      const { data } = await api.post("/users", {
        tenant_id: tenantId,
        email: form.email.trim().toLowerCase(),
        password,
        role: form.role,
      });
      toast.success(`User created for ${data.email}`);
      setLastCreated({ email: data.email, password, role: data.role });
      setForm({ email: "", password: "", role: "truckstop_owner" });
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatError(err, "Create user failed"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded p-5">
        <div className="label-overline mb-3">Add tenant user</div>
        <p className="text-xs text-slate-600 mb-3">
          Creates a user linked to this tenant only. Platform permissions are never granted here.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="label-overline">Email</Label>
            <Input required type="email"
                   data-testid="tenant-user-email"
                   value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="mt-1 rounded h-10 font-mono" />
          </div>
          <div>
            <Label className="label-overline">Role</Label>
            <select data-testid="tenant-user-role"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="mt-1 h-10 w-full border border-slate-300 rounded px-2 font-mono text-sm">
              <option value="truckstop_owner">truckstop_owner</option>
              <option value="truckstop_admin">truckstop_admin</option>
              <option value="truckstop_staff">truckstop_staff</option>
            </select>
          </div>
          <div>
            <Label className="label-overline">Temporary password (optional)</Label>
            <Input type="text" data-testid="tenant-user-password"
                   value={form.password} placeholder="Auto-generate if empty"
                   onChange={(e) => setForm({ ...form, password: e.target.value })}
                   className="mt-1 rounded h-10 font-mono" />
            <div className="text-[11px] text-slate-500 mt-1">
              Share the password over a secure channel. It is shown only once.
            </div>
          </div>
          <Button type="submit" disabled={submitting}
                  data-testid="tenant-user-submit"
                  className="rounded bg-blue-700 hover:bg-blue-800 w-full">
            {submitting ? "Creating…" : "Add user"}
          </Button>
        </form>
        {lastCreated && (
          <div className="mt-4 border border-green-300 bg-green-50 rounded p-3 text-sm"
               data-testid="tenant-user-last-created">
            <div className="label-overline text-green-800">New user credentials</div>
            <div className="mt-1 font-mono text-xs break-all">
              {lastCreated.email} · {lastCreated.role}
            </div>
            <div className="mt-1 font-mono text-xs bg-white border border-green-200 rounded px-2 py-1 break-all"
                 data-testid="tenant-user-last-password">
              {lastCreated.password}
            </div>
            <div className="text-[11px] text-slate-600 mt-1">
              Copy this password now and share it with the user. It will not be shown again.
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 label-overline">
          Users at this tenant
        </div>
        {error ? (
          <div className="p-4 text-xs text-red-700">Could not load users.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl w-full text-sm min-w-[380px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="label-overline text-slate-600">
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500">No users yet</td></tr>
                )}
                {items.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100"
                      data-testid={`tenant-user-row-${u.email}`}>
                    <td className="px-4 py-2 font-mono text-xs break-all">{u.email}</td>
                    <td className="px-4 py-2 font-mono text-xs">{u.role}</td>
                    <td className="px-4 py-2 font-mono text-xs">{u.created_at ? fmtDate(u.created_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
